import { useEffect, useMemo, useState } from 'react';
import { isNil } from 'lodash';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { getGrossIRRColumns } from '../config/GrossIRRColumnConfig';
import { GrossIRRParser } from '../config/GrossIRRParser';
import { GrossIRRRowConfig } from '../config/GrossIRRRowConfig';
import type {
  GrossIRRCellData,
  GrossIRRColumnDefinition,
  GrossIRRInitialObject,
  GrossIRRRowData,
  GrossIRRTableData,
} from '../config/types';

export interface UseGrossIRRSpreadsheetConfigProps {
  fundsGrossIrr: Record<string, number | null>;
  firmGrossIrr: number | null;
  fundIdToName: Record<number, string>;
  firmName: string;
}

type GrossIRRSpreadsheet = SpreadsheetConfig<
  GrossIRRInitialObject,
  GrossIRRColumnDefinition,
  GrossIRRTableData,
  GrossIRRCellData
>;

export const useGrossIRRSpreadsheetConfig = ({
  fundsGrossIrr,
  firmGrossIrr,
  fundIdToName,
  firmName,
}: UseGrossIRRSpreadsheetConfigProps) => {
  const [spreadsheet, setSpreadsheet] = useState<GrossIRRSpreadsheet | null>(null);

  // Transform the data into row format
  const rowData: GrossIRRRowData[] = useMemo(() => {
    const rows: GrossIRRRowData[] = [];

    // Add fund rows
    if (fundsGrossIrr) {
      Object.entries(fundsGrossIrr)
        .filter(([, grossIrr]) => !isNil(grossIrr))
        .sort(([idA], [idB]) => {
          const nameA = fundIdToName[Number(idA)] || `Fund ${idA}`;
          const nameB = fundIdToName[Number(idB)] || `Fund ${idB}`;
          return nameA.localeCompare(nameB);
        })
        .forEach(([fundId, grossIrr]) => {
          rows.push({
            id: `fund-${fundId}`,
            name: fundIdToName[Number(fundId)] || `Fund ${fundId}`,
            grossIrr,
            isTotal: false,
          });
        });
    }

    // Add firm total row
    if (!isNil(firmGrossIrr)) {
      rows.push({
        id: 'firm-total',
        name: firmName,
        grossIrr: firmGrossIrr,
        isTotal: true,
      });
    }

    return rows;
  }, [fundsGrossIrr, firmGrossIrr, fundIdToName, firmName]);

  const rowConfig = useMemo(() => GrossIRRRowConfig(rowData), [rowData]);
  const columns = useMemo(() => getGrossIRRColumns(), []);

  useEffect(() => {
    if (rowData.length === 0) {
      setSpreadsheet(null);
      return;
    }

    setSpreadsheet(
      new SpreadsheetConfig<
        GrossIRRInitialObject,
        GrossIRRColumnDefinition,
        GrossIRRTableData,
        GrossIRRCellData
      >({
        columns,
        name: 'GrossIRRSummary',
        parser: GrossIRRParser,
        rowConfig,
        tableData: { columns },
        format: '',
        showToolbar: false,
        allowDeleteRow: false,
        allowAddRow: false,
      })
    );
  }, [rowData, columns, rowConfig]);

  return {
    spreadsheet,
    hasData: rowData.length > 0,
  };
};
